import type { ColumnDef, PaginationState, Row } from '@tanstack/react-table';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { ReactPaginateProps } from 'react-paginate';
import type { IDataTableNotFoundSectionProps } from '../atoms/DataTableNotFoundSection';

/**
 * Tablo durumları enum'ı.
 * Tablonun hangi durumda olduğunu belirtir ve UI render'ını kontrol eder.
 */
export enum DataTableStatus {
  /** İlk yükleme durumu. Veri henüz çekilmemiş. */
  loading = 'loading',
  /** Veri yeniden çekiliyor (revalidation). Önceki veri gösterilmeye devam eder. */
  fetching = 'fetching',
  /** Veri başarıyla yüklendi ve gösteriliyor. */
  success = 'success',
  /** Veri çekilirken hata oluştu. */
  error = 'error',
  /** Veri başarıyla yüklendi ancak boş (hiç kayıt yok). */
  empty = 'empty',
  /** İlk durum. Henüz hiçbir işlem yapılmadı. */
  initial = 'initial',
}

/**
 * Filtre objesi interface'i.
 * Key-value çiftleri olarak filtreleri tutar.
 * Örnek: { status: 'active', category: 'electronics', minPrice: 100 }
 */
export interface IDataTableFilters {
  [key: string]: any;
}

/**
 * React Query query key tipi.
 * String, number veya null değerlerinden oluşan array.
 * Örnek: ['users', 123, 'active', 'page=1&limit=10']
 */
export type TDataTableQueryKey = (string | number | null)[];

/**
 * Tablo seviyesinde geçici veri tipi.
 * Key-value çiftleri olarak verileri tutar.
 * Örnek: { selectedIds: [1, 2, 3] }
 */
export type TDataTableTempData = Record<string | number, any>;

/**
 * Satır seviyesinde geçici veri tipi.
 * Key-value çiftleri olarak verileri tutar.
 * Örnek: { isEditing: true, originalData: { id: 1, name: 'John Doe' } }
 */
export type TDataTableRowTempData = Record<string | number, any>;

/**
 * Pagination durumu interface'i.
 * TanStack Table'ın PaginationState tipini extend eder.
 * - pageIndex: Hangi sayfada olduğumuz (0-based index)
 * - pageSize: Sayfa başına gösterilecek kayıt sayısı
 */
export interface IDataTablePagination extends PaginationState {}

/**
 * Pagination meta bilgileri.
 * Pagination component'lerinin ihtiyaç duyduğu hesaplanmış değerler.
 */
export interface IDataTablePaginationMeta {
  /** Mevcut sayfadaki kayıt sayısı */
  itemCount: number;
  /** Sayfa başına gösterilecek kayıt sayısı */
  pageSize: number;
  /** Toplam sayfa sayısı (Math.ceil(totalCount / pageSize)) */
  pageCount: number;
}

/**
 * Veri çekme fonksiyonuna gönderilen parametreler.
 * queryFn fonksiyonu bu parametreleri alır.
 */
export interface IDataTableQueryFnParams {
  /** Vendor ID. Redux store'dan otomatik olarak alınır. */
  vendorId: string | number | null;

  /** Aktif filtreler. Kullanıcı tarafından setFilters ile güncellenir. */
  filters: IDataTableFilters;

  /** Mevcut pagination durumu. pageIndex ve pageSize içerir. */
  pagination: IDataTablePagination;
}

/**
 * API'den gelen yanıtın normalize edilmiş hali.
 * responseAdapter fonksiyonu API yanıtını bu formata çevirmelidir.
 *
 * @template TData - Tabloda gösterilecek veri tipi
 */
export interface IDataTableNormalizedResponse<TData extends object = any> {
  /** Mevcut sayfadaki veri listesi */
  items: (TData | (TData & { _rowId: string | number | null }))[];
  /** Tüm veritabanındaki toplam kayıt sayısı (tüm sayfalar dahil) */
  totalCount: number;
  /** Mevcut sayfadaki kayıt sayısı (genellikle items.length ile aynı) */
  itemCount: number;
  /** Toplam sayfa sayısı (Math.ceil(totalCount / limit)) */
  pageCount: number;
  /** Mevcut sayfa numarası (1-based, pageIndex + 1) */
  currentPage: number;
  /** Sayfa başına gösterilecek kayıt sayısı (pageSize ile aynı) */
  limit: number;
}

/**
 * DataTable component'inin ref üzerinden erişilebilen API'si.
 * useRef<IDataTableRef>() ile oluşturulur ve component'e ref prop'u olarak verilir.
 *
 * @example
 * ```tsx
 * const tableRef = useRef<IDataTableRef>(null);
 *
 * <DataTable ref={tableRef} ... />
 *
 * // Filtre ekle
 * tableRef.current?.setFilters({ status: 'active' });
 *
 * // Bir sonraki sayfaya geç
 * tableRef.current?.nextPage();
 * ```
 */
export interface IDataTableRef {
  /** Mevcut React Query query key'i. Cache invalidation için kullanılabilir. */
  queryKey: TDataTableQueryKey;

  /**
   * ========================================================
   * Filtre yönetimi
   * ========================================================
   */

  /** Mevcut aktif filtreler */
  filters: IDataTableFilters;
  /** Filtreleri günceller. State veya updater fonksiyon kabul eder. */
  setFilters: Dispatch<SetStateAction<IDataTableFilters>>;
  /** Tüm filtreleri sıfırlar (boş obje yapar). */
  resetFilters: () => void;

  /**
   * ========================================================
   * Pagination yönetimi
   * ========================================================
   */

  /** Mevcut pagination durumu (pageIndex, pageSize) */
  pagination: IDataTablePagination;
  /** Pagination durumunu günceller. State veya updater fonksiyon kabul eder. */
  setPagination: Dispatch<SetStateAction<IDataTablePagination>>;
  /** Bir sonraki sayfaya geçer. */
  nextPage: () => void;
  /** Bir önceki sayfaya geçer. */
  previousPage: () => void;
  /** Pagination'ı başlangıç değerine döndürür (tablo varsayılanı + `initial.pagination`). */
  resetPagination: () => void;
  /** Sayfa başına gösterilecek kayıt sayısını ayarlar. Minimum 1 olmalıdır. */
  setLimit: (limit: number) => void;

  /**
   * ========================================================
   * Satır seçimi (row selection)
   * ========================================================
   * enableRowSelection: true ise kullanılabilir.
   */

  /** Seçili satırların ID → true eşlemesi (TanStack Table rowSelection state). */
  rowSelection: Record<string, boolean>;
  /** Seçili satırların orijinal verilerini (row.original) döndürür. */
  getSelectedRows: <T = any>() => T[];
  /** Seçimi günceller. State veya updater fonksiyon kabul eder. */
  setRowSelection: Dispatch<SetStateAction<Record<string, boolean>>>;
  /** Tüm satır seçimini temizler. */
  clearSelection: () => void;

  /**
   * Tablo verisini yeniden çeker (React Query invalidate).
   * useDataTableUtilityFunctions hook'undaki aynı fonksiyon; ref üzerinden üst bileşenin kullanımı için açılmıştır.
   * table.options.meta.invalidateDataTableQuery ile aynı referans.
   */
  invalidateDataTableQuery: IDataTableUtilityFunctions['invalidateDataTableQuery'];
}

/**
 * Page size select box seçenekleri.
 * Her seçenek bir değer ve görüntülenecek label içerir.
 */
export interface IDataTablePageSizeSelectBoxOptions {
  /** Select option'ın value değeri (string veya number) */
  value: string | number;
  /** Select option'da görüntülenecek metin */
  label: string;
}

/**
 * Page size select box component'ine özel props.
 * DataTablePageSizeSelectBox component'i tarafından kullanılır.
 */
export interface IDataTablePageSizeSelectBoxSharedProps {
  /** Select wrapper div'i için CSS sınıfı */
  wrapperClassName?: string;
  /** Select elementi için CSS sınıfı (varsayılan: 'form-select') */
  selectClassName?: string;
  /** Gösterilecek sayfa boyutu seçenekleri. Varsayılan: [10, 20, 50, 100] */
  options?: IDataTablePageSizeSelectBoxOptions[];
}

/**
 * Pagination component'ine özel props.
 * ReactPaginate props'larını genişletir ancak pageCount, onPageChange ve forcePage
 * prop'larını kaldırır (bunlar DataTable tarafından otomatik yönetilir).
 */
export interface IDataTablePaginationSharedProps extends Omit<ReactPaginateProps, 'pageCount' | 'onPageChange' | 'forcePage'> {
  /**
   * ========================================================
   * Previous Label/Button/etc. Özelleştirmeleri
   * ========================================================
   */
  /** Previous wrapper div'i için CSS sınıfı */
  previousLabelWrapperClassName?: string;
  /** Previous button için CSS sınıfı */
  previousLabelBtnClassName?: string;
  /** Previous icon için CSS sınıfı */
  previousLabelIconClassName?: string;

  /**
   * ========================================================
   * Next Label/Button/etc. Özelleştirmeleri
   * ========================================================
   */
  /** Next wrapper div'i için CSS sınıfı */
  nextLabelWrapperClassName?: string;
  /** Next button için CSS sınıfı */
  nextLabelBtnClassName?: string;
  /** Next icon için CSS sınıfı */
  nextLabelIconClassName?: string;
}

/**
 * Pagination component'lerinin ortak props'ları.
 * DataTablePagination ve DataTablePageSizeSelectBox tarafından kullanılır.
 */
export interface IDataTablePaginationComponentCommonProps {
  /** Pagination durumunu güncelleme fonksiyonu */
  setPagination: IDataTableRef['setPagination'];
  /** Mevcut pagination durumu */
  paginationState: IDataTablePagination;
  /** Pagination meta bilgileri (itemCount, pageCount, pageSize) */
  paginationMeta: IDataTablePaginationMeta;
}

/**
 * DataTable component'inin farklı bölümlerine özel CSS sınıfları.
 * classNames prop'u ile DataTable'a verilir.
 */
export interface IDataTableClassNames {
  /** Tablo wrapper div'i için CSS sınıfı */
  wrapper?: string;
  /** <table> elementi için CSS sınıfı */
  table?: string;
  /** <thead> elementi için CSS sınıfı */
  tableHeader?: string;
  /** <thead> içindeki <tr> elementi için CSS sınıfı */
  tableHeaderRow?: string;
  /** <tbody> elementi için CSS sınıfı */
  tableBody?: string;
  /** <tbody> içindeki <tr> elementi için CSS sınıfı */
  tableBodyRow?: string;
  /** Footer wrapper div'i için CSS sınıfı */
  footer?: string;
  /** Pagination wrapper div'i için CSS sınıfı */
  paginationWrapper?: string;
  /** Page size select box wrapper div'i için CSS sınıfı */
  pageSizeWrapper?: string;
}

export enum DataTableNotFoundMode {
  showInsteadOfTable = 'show-instead-of-table',
  showWithinTable = 'show-within-table',
}

export interface IDataTableNotFoundOptions extends IDataTableNotFoundSectionProps {
  mode?: DataTableNotFoundMode;
}

/**
 * DataTable component'inin ana props interface'i.
 *
 * @template TData - Tabloda gösterilecek veri tipi
 * @template TQueryFn - Veri çekme fonksiyonunun tipi
 */
export interface IDataTableProps<TData extends object, TQueryFn extends (params: IDataTableQueryFnParams) => Promise<any>> {
  /**
   * TanStack Table kolon tanımları.
   * Her kolon için header, accessorKey, cell renderer vb. tanımlanabilir.
   * ColumnDef tipinde olmalıdır.
   */
  columns: ColumnDef<any, any>[];

  /**
   * React Query query key.
   * String array veya fonksiyon olabilir.
   * Fonksiyon olarak verilirse, params'a göre dinamik key oluşturulur.
   * Örnek: ['users'] veya (params) => ['users', params.filters.status]
   */
  queryKey: TDataTableQueryKey | ((params: IDataTableQueryFnParams) => TDataTableQueryKey);

  /**
   * API'den veri çeken async fonksiyon.
   * IDataTableQueryFnParams alır ve Promise döndürür.
   * Her filtre/pagination değişikliğinde otomatik olarak çağrılır.
   */
  queryFn: TQueryFn;

  /**
   * API yanıtını IDataTableNormalizedResponse formatına çeviren opsiyonel fonksiyon.
   * API yanıtı standart alanları (items, totalCount, itemCount, pageCount, currentPage, limit)
   * içermiyorsa bu fonksiyon ile dönüştürülmelidir.
   *
   * @example
   * responseAdapter={(response) => ({
   *   items: response.data.list,
   *   totalCount: response.data.pagination.total,
   *   itemCount: response.data.list.length,
   *   pageCount: response.data.pagination.pages,
   *   currentPage: response.data.pagination.page,
   *   limit: response.data.pagination.pageSize,
   * })}
   */
  responseAdapter?: (response: Awaited<ReturnType<TQueryFn>>) => IDataTableNormalizedResponse<TData>;

  /**
   * Filtre nesnesini (filters) API'ye uygun şekilde serialize eden opsiyonel fonksiyon.
   * Genellikle filtre objesini string/eşleme çevirip queryFn'e iletmek için kullanılır.
   * Örnek kullanım: filterSerializer={(filters) => ({...filters, extraQuery: 'xyz'})}
   *
   * @param filters - Güncel filtreler (IDataTableFilters)
   * @returns API'ye gönderilecek şekilde dönüştürülmüş obje
   */
  filterSerializer?: (filters: IDataTableFilters) => Record<string, any>;

  /**
   * Component ilk render edildiğinde kullanılacak başlangıç değerleri.
   */
  initial?: {
    /** Başlangıç filtreleri. Örnek: { status: 'active', category: 'electronics' } */
    filters?: Record<string, any>;
    /** Başlangıç pagination durumu. Varsayılan: { pageIndex: 1, pageSize: 10 } */
    pagination?: Partial<IDataTablePagination>;
  };

  /**
   * Tablonun farklı bölümlerine özel CSS sınıfları.
   * Detaylar için IDataTableClassNames interface'ine bakın.
   */
  classNames?: IDataTableClassNames;

  /**
   * Pagination component'ine özel props.
   * Detaylar için IDataTablePaginationSharedProps interface'ine bakın.
   */
  paginationProps?: IDataTablePaginationSharedProps;

  /**
   * Page size select box component'ine özel props.
   * Detaylar için IDataTablePageSizeSelectBoxSharedProps interface'ine bakın.
   */
  pageSizeProps?: IDataTablePageSizeSelectBoxSharedProps;

  /**
   * Not found options.
   * Detaylar için IDataTableNotFoundOptions interface'ine bakın.
   */
  notFoundOptions?: IDataTableNotFoundOptions;

  /**
   * Her bir satır için benzersiz bir ID döndüren opsiyonel fonksiyon.
   * TanStack Table'ın `getRowId` fonksiyonuna karşılık gelir.
   * Eğer belirtilmezse, row.original.id/id alanı otomatik kullanılır.
   *
   * @param row - TanStack Table'dan gelen Row nesnesi
   * @returns satırın benzersiz anahtar değeri (string | number | null)
   *
   * Örnek kullanım:
   * rowIdSelector={(row) => row.original.productId}
   */
  rowIdSelector?: (row: Row<any>) => string | number | null;

  /**
   * Pagination state'ini URL search params ile senkronize eder.
   * true olduğunda, page ve limit değerleri URL'den okunur ve değişiklikler URL'e yansıtılır.
   * Browser back/forward butonlarıyla uyumlu çalışır.
   * Örnek URL: /products/my-products/all?page=2&limit=20
   */
  syncWithUrl?: boolean;

  /**
   * Satır seçimi (checkbox) özelliğini açar.
   * true ise ref üzerinden getSelectedRows, setRowSelection, clearSelection kullanılabilir
   * ve kolonlara DataTableSelectCheckbox ile seçim kolonu eklenebilir.
   */
  enableRowSelection?: boolean;

  /**
   * Satır seçimi değiştiğinde çağrılır (örn. üst bileşende seçim sayısı göstermek için).
   */
  onRowSelectionChange?: (rowSelection: Record<string, boolean>) => void;

  /**
   * Hangi satırların genişletilebilir (accordion) olduğunu belirler.
   * TanStack Table'ın `getRowCanExpand` fonksiyonuna karşılık gelir.
   * Verilmezse hiçbir satır genişletilemez (varsayılan davranış değişmez).
   *
   * Cell renderer'lar genişlemeyi `info.row.getCanExpand()` / `info.row.toggleExpanded()` /
   * `info.row.getIsExpanded()` ile kontrol edebilir.
   *
   * @example getRowCanExpand={(row) => row.original.state === 'accepted'}
   */
  getRowCanExpand?: (row: Row<TData>) => boolean;

  /**
   * Genişletilmiş (expanded) bir satırın hemen altında render edilecek içerik.
   * Yalnızca `getRowCanExpand` true dönen ve genişletilmiş satırlar için çağrılır.
   * Tüm kolonları kapsayan tek bir hücre (colSpan) içinde gösterilir.
   *
   * @example renderSubRow={(row) => <OfferDetail item={row.original} />}
   */
  renderSubRow?: (row: Row<TData>) => ReactNode;

  /**
   * Expanded row'un solunda placeholder hücre oluşturmak için expander kolon genişliği.
   * Verilirse, expanded row content bu genişlik kadar sağa kayar (ör: "50px").
   * Verilmezse expanded row tüm genişliği kaplar (varsayılan davranış).
   * Genellikle expander kolon var olduğunda kullanılır.
   *
   * @example expanderColumnWidth="50px"
   */
  expanderColumnWidth?: string;
}

/**
 * Tablo utility fonksiyonları interface'i.
 * Bu fonksiyonlar table.options.meta üzerinden erişilebilir.
 * Özellikle cell renderer'lar içinde kullanışlıdır.
 */
export interface IDataTableUtilityFunctions {
  /** React Query query'i geçersiz kılar. */
  invalidateDataTableQuery: () => void;

  /**
   * ========================================================
   * Satır yönetimi
   * ========================================================
   */

  /** Belirtilen koşula uyan satırları tablodan kaldırır. */
  removeRow: (selector: (row: Row<any>) => boolean) => void;

  /**
   * ========================================================
   * Tablo seviyesi geçici veri yönetimi
   * ========================================================
   */

  /** Tablo seviyesinde geçici veri saklar. */
  setTableTempData: (
    data: any,
    opts?: {
      /** true ise mevcut veriyle birleştirir, false ise üzerine yazar. Varsayılan: true */
      merge?: boolean;
    },
  ) => void;
  /** Tablo seviyesinde saklanan geçici veriyi getirir. */
  getTableTempData: (fallback?: any) => any;
  /** Tablo seviyesinde saklanan tüm geçici veriyi temizler. */
  clearTableTempData: () => void;

  /**
   * ========================================================
   * Satır seviyesi geçici veri yönetimi
   * ========================================================
   */

  /** Belirli bir satır için geçici veri saklar. */
  setRowTempData: (row: Row<any>, data: any) => void;
  /** Belirli bir satır için saklanan geçici veriyi getirir. */
  getRowTempData: (row: Row<any>, fallback?: any) => any;
  /** Belirli bir satır için saklanan geçici veriyi siler. */
  deleteRowTempData: (row: Row<any>) => void;
  /** Tüm satırlar için saklanan geçici verileri temizler. */
  clearAllRowTempData: () => void;
}
